import type {
  AdminSessionStatus,
  AdminSessionView,
  AmbianceAsset,
  AmbianceAssetList,
  ChatConsentView,
  ChatMessageAccepted,
  ChatMessageInput,
  ChatRoomCredentials,
  ChatRoomList,
  ChatRoomTranscript,
  ChatRoomView,
  ChatTranscript,
  CriterionDetail,
  CriterionList,
  CriterionSaveDraft,
  CriterionSaveVersion,
  CriterionVersion,
  ExperienceCheck,
  ExperienceCheckRequest,
  ExperienceDetail,
  ExperienceList,
  ExperienceSaveDraft,
  ExperienceSaveVersion,
  ExperienceVersion,
  InvolvementLevelBlockRow,
  InvolvementLevelList,
  MethodDetail,
  MethodSaveDraft,
  MethodSaveVersion,
  MethodVersion,
  ModelCatalogView,
  ModelParams,
  ModelParamsView,
  ModelSettingsDetail,
  ModelSettingsSaveDraft,
  ModelSettingsSaveVersion,
  ModelSettingsVersion,
  ModelTestView,
  MovePointerRequest,
  NoyauTextsDetail,
  NoyauTextsSaveDraft,
  NoyauTextsSaveVersion,
  NoyauTextsUsage,
  NoyauTextsVersion,
  NpcBench,
  NpcBenchList,
  NpcBenchWrite,
  NpcDeleted,
  NpcDetail,
  NpcEstimate,
  NpcLaunch,
  NpcList,
  NpcPlayback,
  NpcPrefill,
  NpcReferents,
  NpcReplayList,
  NpcSaveDraft,
  NpcSaveVersion,
  NpcSeriesDetail,
  NpcSeriesLaunched,
  NpcSeriesList,
  NpcVersion,
  NpcWatch,
  PersonaDetail,
  PersonaList,
  PersonaSaveDraft,
  PersonaSaveVersion,
  PersonaToneBlockRow,
  PersonaToneEntry,
  PersonaToneList,
  PersonaToneWrite,
  PersonaVersion,
  ProtocolDetail,
  ProtocolList,
  ProtocolSaveDraft,
  ProtocolSaveVersion,
  ProtocolVersion,
  ReferentPlan,
  RuleSettingsDetail,
  RuleSettingsSaveDraft,
  RuleSettingsSaveVersion,
  RuleSettingsVersion,
  VersionTag,
} from '@matter_extractor/shared';
import {
  METHOD_ID,
  MODEL_SETTINGS_ID,
  NOYAU_TEXTS_ID,
  RULE_SETTINGS_ID,
  adminSessionStatusSchema,
  adminSessionViewSchema,
  ambianceAssetListSchema,
  ambianceAssetSchema,
  chatConsentViewSchema,
  chatMessageAcceptedSchema,
  chatRoomCredentialsSchema,
  chatRoomListSchema,
  chatRoomTranscriptSchema,
  chatRoomViewSchema,
  chatTranscriptSchema,
  criterionDetailSchema,
  criterionListSchema,
  criterionVersionSchema,
  experienceCheckSchema,
  experienceDetailSchema,
  experienceListSchema,
  experienceVersionSchema,
  involvementLevelListSchema,
  methodDetailSchema,
  methodVersionSchema,
  modelCatalogViewSchema,
  modelParamsViewSchema,
  modelSettingsDetailSchema,
  modelSettingsVersionSchema,
  modelTestViewSchema,
  noyauTextsDetailSchema,
  noyauTextsUsageSchema,
  noyauTextsVersionSchema,
  npcBenchListSchema,
  npcBenchSchema,
  npcDeletedSchema,
  npcDetailSchema,
  npcEstimateSchema,
  npcListSchema,
  npcPlaybackSchema,
  npcPrefillSchema,
  npcReferentsSchema,
  npcReplayListSchema,
  npcSeriesDetailSchema,
  npcSeriesLaunchedSchema,
  npcSeriesListSchema,
  npcVersionSchema,
  npcWatchSchema,
  personaDetailSchema,
  personaListSchema,
  personaToneEntrySchema,
  personaToneListSchema,
  personaVersionSchema,
  protocolDetailSchema,
  protocolListSchema,
  protocolVersionSchema,
  referentPlanSchema,
  ruleSettingsDetailSchema,
  ruleSettingsVersionSchema,
} from '@matter_extractor/shared';

/**
 * The one address of the backend, and what the coupe left of it (issue #32).
 *
 * Eight families now: the Tahiti door, the blind interview and its
 * administration, the media library, the models, the criteria - the first
 * consumer of the common versioning pattern (refonte modulaire §4, §6) - the
 * personas, the second, with the two reference lists their versions copy from
 * (§11.5), the protocols, the third (§7), and the experiences, the fourth
 * (§9), whose eleventh call is the dry run the save box reads.
 *
 * Every answer is PARSED before it is returned. A response is an external input
 * even when we wrote the server: a shape that drifted is caught here, once, and
 * not in the eleventh component that read a field.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export class ApiError extends Error {
  readonly status: number;
  /**
   * The envelope's `error` field: the machine code of the refusal.
   *
   * A screen words a refusal from the code, never from the server's sentence.
   */
  readonly code: string | null;
  /**
   * The `details` of the error envelope, unparsed.
   *
   * `message` is a DEVELOPER's string - it goes to the logs and to the
   * OpenAPI, never under an operator's eyes. What a screen is allowed to word
   * is the `code` inside these details, which is why they have to survive the
   * wire instead of being flattened into a sentence.
   */
  readonly details: unknown;

  constructor(status: number, message: string, details?: unknown, code?: string | null) {
    super(message);
    this.status = status;
    this.details = details;
    this.code = code ?? null;
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, {
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      /*
        TWO shapes of error body reach here, and `details` has to carry the
        refusal out of both.

        The generic envelope of `middleware/error-handler.ts` nests it -
        `{ error, message, details }` - and that is what a Zod 400 and the model
        refusals arrive as. A BUSINESS refusal does not: `versioning/app.ts` and
        the two reference-list modules answer `c.json(err.refusal, 409)`, so the
        contract body IS the body - `{ error, code, name? }` - with no `details`
        key at all.

        Read as `body.details` alone, every one of those refusals arrived here
        as `undefined`, `versionedRefusalOf` answered null, and the six codes of
        §4 that exist so a screen can word each one differently all rendered as
        the same generic sentence. Nothing caught it: the frontend tests build
        their `ApiError` by hand with the details already unwrapped, and no e2e
        spec asserts a refusal's wording. Found in issue #41, while wiring the
        second consumer onto the same path.

        The fallback is safe in the other direction: an envelope that carries
        `details` keeps them, and a body that is neither shape fails the
        refusal schema's `safeParse` and reads as « no typed refusal », which is
        the honest answer for a 500.
      */
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// The blind interview, as the person plays it (chat spec §5 to §11)
// ---------------------------------------------------------------------------

/**
 * The blind-interview room behind a token (chat spec §5).
 *
 * The token travels IN THE PATH and never in the query, the line this repo
 * already holds for the administration cookie: a secret in a query string leaks
 * through the `Referer`, the browser history and every proxy log.
 *
 * A 410 means expired OR unknown, and the caller must not try to tell them
 * apart - S8 is one screen for both (§5).
 */
export function getChatRoom(token: string): Promise<ChatRoomView> {
  return request(`/api/chat/${token}`).then((body) => chatRoomViewSchema.parse(body));
}

/**
 * The consent block in force for this room (chat spec §6).
 *
 * A call of its own rather than three more fields on the room: the room is
 * re-asked every ~1500 ms while this text moves a handful of times a year, and
 * the conversation and end screens have no paragraph to render. Served rather
 * than imported, the régime of BUILD-829 - a rewording reaches the next guest
 * instead of the next deploy of the bundle.
 *
 * It carries no fingerprint. What the room records is computed server-side from
 * the same base read, so nothing a caller could choose ever names the text she
 * accepted.
 */
export function getChatConsent(token: string): Promise<ChatConsentView> {
  return request(`/api/chat/${token}/consent`).then((body) => chatConsentViewSchema.parse(body));
}

/** Ticks the box for the role the token names, and stamps the room's fingerprint (§6). */
export function postChatConsent(token: string): Promise<ChatRoomView> {
  return request(`/api/chat/${token}/consent`, { method: 'POST' }).then((body) =>
    chatRoomViewSchema.parse(body),
  );
}

/**
 * Un-ticks the box (§6, issue #21).
 *
 * Refused with a 409 once this role is READY - the order of unwinding is the
 * server's rule, not the screen's: cancel the ready first, then the consent.
 * A 409 also comes back once the room has left `open`, like its four neighbours.
 */
export function deleteChatConsent(token: string): Promise<ChatRoomView> {
  return request(`/api/chat/${token}/consent`, { method: 'DELETE' }).then((body) =>
    chatRoomViewSchema.parse(body),
  );
}

/**
 * « Je suis prêt » (§4.4). No body: the role comes from the token, and being
 * ready is not a value - it is a moment, and the server is the one holding the
 * clock that decides it.
 */
export function postChatReady(token: string): Promise<ChatRoomView> {
  return request(`/api/chat/${token}/ready`, { method: 'POST' }).then((body) =>
    chatRoomViewSchema.parse(body),
  );
}

/** The discreet cancel of §4.4. Refused with a 409 once the conversation has opened. */
export function deleteChatReady(token: string): Promise<ChatRoomView> {
  return request(`/api/chat/${token}/ready`, { method: 'DELETE' }).then((body) =>
    chatRoomViewSchema.parse(body),
  );
}

/**
 * The history of a room, in the order of arrival.
 *
 * The REST read stays the source of truth beside the socket (§7): it serves the
 * first render, the resynchronisation after a reconnection and the export.
 */
export function getChatMessages(token: string, since?: number): Promise<ChatTranscript> {
  // `since` is the highest `seq` the caller already holds. Absent, the answer is
  // the whole history - the first render and the export; present, it is the tail
  // the client is missing, which is what a reconnection asks for.
  const cursor = since === undefined ? '' : `?since=${since}`;
  return request(`/api/chat/${token}/messages${cursor}`).then((body) =>
    chatTranscriptSchema.parse(body),
  );
}

/**
 * Posts one message (chat spec §8).
 *
 * The write is a POST and not a socket frame, and the argument in full is in
 * `routes/chat-messages.ts`: the socket carries no role, and a status code is
 * what tells the client-side queue a temporary refusal from a permanent one.
 *
 * Idempotent on `clientMessageId`: a re-send after a lost acknowledgement is a
 * 201 carrying the STORED message and `duplicate: true`, never an error and
 * never a second row (§8.1).
 */
export function postChatMessage(
  token: string,
  input: ChatMessageInput,
): Promise<ChatMessageAccepted> {
  return request(`/api/chat/${token}/messages`, {
    method: 'POST',
    body: JSON.stringify(input),
  }).then((body) => chatMessageAcceptedSchema.parse(body));
}

/**
 * The websocket address of a room's delta channel (§7).
 *
 * Derived from `API_URL` rather than declared, so there is ONE notion of where
 * the backend is. Two cases, and both are real deployments of this repo:
 *
 *   - `.env.production` ships an EMPTY `VITE_API_URL`, so the call goes
 *     same-origin through nginx - whose `location /api/` already carries the
 *     `$connection_upgrade` map. The scheme has to follow the page's, or an
 *     `https` page opening a `ws://` socket is blocked as mixed content.
 *   - in `make dev` the backend is on another port, and the `http://` prefix
 *     becomes `ws://`.
 *
 * The token sits in the PATH here too (§5): a secret in a query string leaks
 * through the `Referer`, the history and the proxy logs, and a websocket
 * handshake is an HTTP request like any other.
 */
export function chatSocketUrl(token: string): string {
  const base = API_URL === '' ? window.location.origin : API_URL;
  // `http` -> `ws` and `https` -> `wss` in one substitution: the `s` is what
  // makes the second case work, and getting it wrong is a page that loads and a
  // socket that never opens.
  return `${base.replace(/^http/, 'ws')}/api/chat/${token}/ws`;
}

/**
 * Close the interview (chat spec §11) - and it answers 403 on a guest's token.
 *
 * No role in the call, and nothing to put one in: the server takes it off the
 * resolved token (§5), so a caller cannot claim to be the interviewer. The
 * refusal is therefore not something this function can prevent or provoke - it
 * is a property of which link the browser is holding.
 *
 * It hands back the room as the interviewer now sees it, so the screen moves
 * straight to S7 without a second round trip. Calling it twice is harmless -
 * the backend keeps the first `closedAt` - which is what makes a double submit
 * on a bad connection cost nothing.
 */
export function closeChatRoom(token: string): Promise<ChatRoomView> {
  return request(`/api/chat/${token}/close`, { method: 'POST' }).then((body) =>
    chatRoomViewSchema.parse(body),
  );
}

// ---------------------------------------------------------------------------
// The administration screens, behind the Tahiti cookie
// ---------------------------------------------------------------------------

/**
 * The credential travels in a cookie and NEVER in the URL: putting it in a
 * query string would write it into the browser history of whoever opens the
 * screen, and into every proxy log on the way.
 */
const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

/**
 * The account door (auth Tahiti §5). The ONE administration call that is not
 * behind the guard - it is the door, and a guarded door would be a key needed
 * to ask for a key.
 *
 * The form posts HERE and never on Tahiti from the browser (T2): the credential
 * has to land in a cookie the browser cannot read, which only our own backend
 * can set. Nothing of what is typed is kept on this side - the answer names the
 * operator and carries no token, by design of `adminSessionViewSchema`.
 */
export function openAdminSession(email: string, password: string): Promise<AdminSessionView> {
  return request('/api/admin/session', {
    ...ADMIN_INIT,
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }).then((body) => adminSessionViewSchema.parse(body));
}

/**
 * Who is connected, or `null` for a caller that came in by the shared token.
 *
 * The cheap validation endpoint that lets the shell decide between the door and
 * the screens on its FIRST render instead of waiting for a screen to be
 * refused. A 401 here means nobody is connected.
 */
export function fetchAdminSessionStatus(): Promise<AdminSessionStatus> {
  return request('/api/admin/session', ADMIN_INIT).then((body) =>
    adminSessionStatusSchema.parse(body),
  );
}

/**
 * Closes the session. The cookie is `httpOnly`, so the browser cannot drop it
 * on its own - logging out is a round trip or it does not happen at all.
 *
 * NOT through `request`: the route answers 204, and `request` parses a body
 * unconditionally on success. It throws rather than failing quietly, so an
 * operator who is still connected is told rather than shown a door that lets
 * them straight back in.
 */
export async function closeAdminSession(): Promise<void> {
  const response = await fetch(`${API_URL}/api/admin/session`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new ApiError(response.status, 'La déconnexion a échoué.');
}

// ---------------------------------------------------------------------------
// The models (config modeles §2, P5/P6)
// ---------------------------------------------------------------------------

/**
 * Which models the dispositif runs on. Behind the same cookie as the other
 * administration screens, in READING as in writing: this document names every
 * model the deploy talks to, on a deploy that is public.
 *
 * The API KEYS are not here and never will be: they are secrets, they stay in
 * the environment, and a call that could write one would be a call that could
 * exfiltrate one.
 */
export function fetchModelParams(): Promise<ModelParamsView> {
  return request('/api/models/params', ADMIN_INIT).then((body) =>
    modelParamsViewSchema.parse(body),
  );
}

/**
 * The newest Claude models each door serves today (#175), read by the backend
 * from the providers' own lists. Its own call rather than a field of the
 * params view: a provider that is slow to answer must not hold the seats
 * back, and the screen draws them on its built-in tiles meanwhile.
 */
export function fetchModelCatalog(): Promise<ModelCatalogView> {
  return request('/api/models/catalog', ADMIN_INIT).then((body) =>
    modelCatalogViewSchema.parse(body),
  );
}

/**
 * « Tester les rôles » (BUILD-835): one throwaway call per seat, on the values
 * the FORM holds and never on what the base holds.
 *
 * It WRITES NOTHING - no model moves, no journal line is added - which is why
 * it can be fired as often as somebody likes while a form sits unsaved.
 */
export function testModelParams(params: ModelParams): Promise<ModelTestView> {
  return request('/api/models/test', {
    method: 'POST',
    ...ADMIN_INIT,
    body: JSON.stringify({ params }),
  }).then((body) => modelTestViewSchema.parse(body));
}

// ---------------------------------------------------------------------------
// The media library (immersion §6, I3; kept by refonte modulaire §16, H7)
// ---------------------------------------------------------------------------
// Only the public address of a file survived the coupe: the list, the upload
// and the removal had no caller left once the slots screen went (issue #32),
// and the lot that mounts the media picker writes the calls it needs. Issue #57
// mounts it - the fond of §9.4 is a colour or a file of the library - so the
// two it needs are here. The REMOVAL was absent until issue #121, which builds
// the screen that offers it: §9.1 makes it a refusable gesture with its own
// 409 and its list of referents, parsed by the CALLER from `ApiError.details`
// (the régime `linkRefusalOf` states - never read from `ApiError.code`, which
// carries the French sentence on THIS contract shape and not the machine code).

/** The public URL of a file - listenable in an <audio>, viewable in an <img>. */
export function ambianceFileUrl(assetId: string): string {
  return `${API_URL}/api/ambiance/files/${encodeURIComponent(assetId)}`;
}

/**
 * The library, whole (§9.4, issue #57).
 *
 * No query: `filterAmbianceAssets` is a SHARED pure function and the picker
 * spends it on what it already holds, so narrowing over the wire would be a
 * round trip per keystroke and a second chance for the screen and the route to
 * disagree about what an accent matches (`media-picker.tsx`).
 */
export function fetchAmbianceAssets(): Promise<AmbianceAssetList> {
  return request('/api/ambiance/assets', ADMIN_INIT).then((body) =>
    ambianceAssetListSchema.parse(body),
  );
}

/**
 * One file added, from inside the picker (§9.4).
 *
 * `multipart/form-data`, so this is the one administration call that does NOT
 * go through `request`: that helper pins `content-type: application/json`, and
 * a multipart body needs the browser to write its own header WITH the boundary
 * it generated. Setting it by hand is the classic way to post a body no server
 * can split.
 *
 * The license and the tags are facultative on both natures (BUILD-891), and an
 * empty string is what the route reads as « none ».
 */
export async function uploadAmbianceAsset(
  file: File,
  license: string,
  tags: string,
): Promise<AmbianceAsset> {
  const form = new FormData();
  form.append('file', file);
  form.append('license', license);
  form.append('tags', tags);
  const response = await fetch(`${API_URL}/api/ambiance/assets`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!response.ok) {
    // The same parse as `request()` above, and for the same reason: the
    // GENERIC envelope of `middleware/error-handler.ts` - `{ error, message,
    // details }` - is what `routes/ambiance.ts` throws its `HttpError`s as, and
    // `mediatheque-labels.ts`'s `ambianceUploadRefusal` switches on `.code`
    // to word `FILE_REQUIRED`/`UNSUPPORTED_FORMAT`/`FILE_TOO_LARGE`
    // differently. Left unparsed, `.code` stayed `null` forever and every
    // refusal rendered as the same generic sentence, no matter what the
    // server actually refused for.
    let message = 'Le fichier n’a pas pu être téléversé.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return ambianceAssetSchema.parse(await response.json());
}

/**
 * Deletes a file (§9.1, issue #121). Refused in 409 `ASSET_IN_USE` when a
 * tagged version of an experience still cites it - the referents travel in
 * `ApiError.details`, parsed against `ambianceAssetInUseSchema` by the caller.
 *
 * The answer carries the library WHOLE, at the régime of
 * `deleteInvolvementLevel`: a screen that just removed one row re-reads the
 * rest from the same response instead of trusting its own optimistic count.
 */
export function deleteAmbianceAsset(assetId: string): Promise<AmbianceAssetList> {
  return request(`/api/ambiance/assets/${encodeURIComponent(assetId)}`, {
    ...ADMIN_INIT,
    method: 'DELETE',
  }).then((body) => ambianceAssetListSchema.parse(body));
}

// ---------------------------------------------------------------------------
// The administration of the blind-interview rooms (chat spec §12, issue #11)
// ---------------------------------------------------------------------------

/**
 * Strikes a room and hands back its TWO LINKS - the only call in this client
 * that ever sees a chat token (§12).
 *
 * They are answered once and are readable nowhere else, which is why this is
 * the one fetcher here whose result the screen has to HOLD ON TO: there is no
 * second call that could fetch them again, so a screen that dropped the answer
 * would have destroyed a room's links by rendering.
 *
 * No body: a room has nothing to configure - no composition, no prompt, no
 * experience (§9), which is exactly why it is not a session.
 */
export function createChatRoom(): Promise<ChatRoomCredentials> {
  return request('/api/admin/chat/rooms', { ...ADMIN_INIT, method: 'POST' }).then((body) =>
    chatRoomCredentialsSchema.parse(body),
  );
}

/**
 * Every room, most recently struck first, with the two figures S9 shows.
 *
 * `reference` is the erasure search of §12: the guest quotes her room reference
 * to ask for a deletion, so finding it has to be a SEARCH and not a hunt. A
 * malformed one comes back 400 - `isChatReference` is the guard, server-side -
 * so the screen can say « retypez la référence » instead of showing an empty
 * list about a room that is right there.
 */
export function fetchChatRooms(reference?: string): Promise<ChatRoomList> {
  const query =
    reference === undefined || reference === ''
      ? ''
      : `?reference=${encodeURIComponent(reference)}`;
  return request(`/api/admin/chat/rooms${query}`, ADMIN_INIT).then((body) =>
    chatRoomListSchema.parse(body),
  );
}

/**
 * One interview in full, in the order of ARRIVAL (§8.2, §12).
 *
 * It answers long after both links stopped resolving, and that is the point of
 * the screen: §10 keeps the transcript and expires only the door.
 */
export function fetchChatRoomTranscript(roomId: string): Promise<ChatRoomTranscript> {
  return request(`/api/admin/chat/rooms/${roomId}`, ADMIN_INIT).then((body) =>
    chatRoomTranscriptSchema.parse(body),
  );
}

/**
 * The same interview as markdown, for the clipboard.
 *
 * NOT through `request`, which parses JSON unconditionally: the caller puts
 * this on the clipboard rather than saving it, because what the team does with
 * a transcript is paste it into a ticket.
 */
export async function fetchChatRoomMarkdown(roomId: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/admin/chat/rooms/${roomId}/transcript.md`, {
    ...ADMIN_INIT,
  });
  if (!response.ok) throw new ApiError(response.status, 'Le transcript n’a pas pu être exporté.');
  return response.text();
}

// ---------------------------------------------------------------------------
// The versioned objects (refonte modulaire §4; issues #37, #38 and #39)
// ---------------------------------------------------------------------------
// TWELVE calls per object since #185 (eleven since #116), and every one of them but the two reads
// answers with
// the WHOLE entity. That is the pattern's decision and not a convenience: a
// version saved, a pointer moved, a rename, a retirement each change where the
// two pointers sit, what the history holds and whether the draft still differs
// from `développement` - so a write that answered `204` would cost the screen a
// second round trip to learn what its own gesture did.
//
// The business refusals come back as a 409 carrying `versionedErrorSchema` in
// the error envelope. They are NOT unwrapped here: `ApiError.details` keeps
// them intact and `versioned-labels` words each `code` in French. A refusal
// flattened into a sentence here could not be worded differently by the two
// screens that meet it.
//
// The ten are a FACTORY, because they differ by exactly two things: the base
// path, and the three schemas the answers are parsed with. Written out per
// object they would be ten copies of `encodeURIComponent` and `ADMIN_INIT` per
// lot, and the copy that drifted would be the one nobody reads - the retirement
// of the third object, say, posting where it should delete.

/** A parser, so the factory takes a Zod schema without depending on Zod itself. */
type Parser<T> = { parse: (value: unknown) => T };

/** The twelve calls of §4, for one object. */
export interface VersionedApi<List, Detail, Version, SaveVersion, SaveDraft> {
  list: () => Promise<List>;
  create: (input: SaveVersion) => Promise<Detail>;
  read: (id: string) => Promise<Detail>;
  rename: (id: string, name: string) => Promise<Detail>;
  saveDraft: (id: string, input: SaveDraft) => Promise<Detail>;
  discardDraft: (id: string, baseUpdatedAt: string | null) => Promise<Detail>;
  saveVersion: (id: string, input: SaveVersion) => Promise<Detail>;
  /**
   * The referents the save box offers to update (vitrine.md §3.3): who pins an
   * older version of the object, who follows `latest`. The route is mounted for
   * the four objects other objects cite, and not for the experience.
   */
  referents: (id: string) => Promise<ReferentPlan>;
  readVersion: (id: string, versionId: string) => Promise<Version>;
  movePointer: (id: string, input: MovePointerRequest) => Promise<Detail>;
  retire: (id: string) => Promise<Detail>;
  restore: (id: string) => Promise<Detail>;
  duplicate: (id: string, tag: VersionTag) => Promise<Detail>;
}

/**
 * The twelve calls of one versioned object, from its base path and its schemas.
 *
 * `basePath` is `/api/criteria`; the five gestures hang off it exactly as the
 * routes do - one address per gesture, because a `PUT` of a whole entity would
 * hide which of the four just happened and each has its own refusal (§4).
 */
export function createVersionedApi<List, Detail, Version, SaveVersion, SaveDraft>(config: {
  basePath: string;
  list: Parser<List>;
  detail: Parser<Detail>;
  version: Parser<Version>;
}): VersionedApi<List, Detail, Version, SaveVersion, SaveDraft> {
  const { basePath } = config;
  const at = (id: string, suffix = '') => `${basePath}/${encodeURIComponent(id)}${suffix}`;
  const detail = (body: unknown) => config.detail.parse(body);
  const write = (path: string, method: string, body?: unknown) =>
    request(path, {
      ...ADMIN_INIT,
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }).then(detail);

  return {
    /**
     * Every entity, retired ones INCLUDED, and filtered in the page.
     *
     * The decision belongs to the object's list schema and is quoted there: a
     * bibliothèque is a handful of rows, « utilisé dans » is computed at read
     * time anyway, and the filter set of §4 is thirteen dimensions deep - so
     * thirteen query parameters would buy a round trip per keystroke and cost a
     * pure module that can be tested without a base.
     */
    list: () => request(basePath, ADMIN_INIT).then((body) => config.list.parse(body)),

    /**
     * The birth of an entity: its v1 written in the same transaction (§4).
     *
     * The same body as a later version, because it IS a later version - the
     * first one. Before this call there is neither entity nor server draft, so
     * what the empty editor holds lives only in the page (§6).
     */
    create: (input) => write(basePath, 'POST', input),

    /** Everything the editor reads in one call: the pointers, the history, the draft, the usage. */
    read: (id) => request(at(id), ADMIN_INIT).then(detail),

    /**
     * Renaming - the one write of the pattern that does NOT create a version
     * (§4).
     *
     * It touches the entity AND the draft, so the editor does not also have to
     * autosave the new name for it to survive a reload. Refused with a 409
     * `name-taken` that NAMES the other entity.
     */
    rename: (id, name) => write(at(id), 'PATCH', { name }),

    /**
     * One autosave of the server draft (§4).
     *
     * `baseUpdatedAt` is the stamp of the draft this editor read - `null` when
     * it read none. Last write wins, but only over a draft the writer had
     * actually seen: the server answers 409 `draft-stale` when its own is
     * newer, and the editor then offers to reload rather than overwriting
     * somebody's paragraph.
     */
    saveDraft: (id, input) => write(at(id, '/draft'), 'PUT', input),

    /**
     * « Abandonner le brouillon » (issue #185): the server draft is deleted and
     * the editor starts again from the `développement` version.
     *
     * Guarded like the autosave, by the stamp of the draft this editor read:
     * `baseUpdatedAt` rides the QUERY STRING because a `DELETE` carries no body,
     * and an absent parameter means « I read no draft ». The server answers 409
     * `draft-stale` when its own draft moved since, and the entity like every
     * other write - without a draft - when it holds none, which is a success
     * with no effect rather than a refusal.
     */
    discardDraft: (id, baseUpdatedAt) =>
      write(
        at(
          id,
          baseUpdatedAt === null
            ? '/draft'
            : `/draft?baseUpdatedAt=${encodeURIComponent(baseUpdatedAt)}`,
        ),
        'DELETE',
      ),

    /**
     * « Enregistrer une nouvelle version », with the two gestures the box
     * confirms together: the version, and optionally the move of `production`
     * onto it.
     *
     * A 409 `no-change` is what keeps a version empty of change from existing
     * at all - the editor greys the button on the same comparison, so this
     * refusal is the guard behind the courtesy and not the first line of
     * defence.
     */
    saveVersion: (id, input) => write(at(id, '/versions'), 'POST', input),

    /**
     * The plan of the referents the save box draws (vitrine.md §3.3, H22): read
     * when the box opens, writes nothing, and answers for the version the save
     * is about to write.
     */
    referents: (id) =>
      request(at(id, '/referents'), ADMIN_INIT).then((body) => referentPlanSchema.parse(body)),

    /**
     * One version read in full, for « Voir » and for « Repartir de cette
     * version » (§4).
     *
     * Its own call rather than a field of the detail: the detail already
     * carries the content of `développement` and of the draft, and shipping the
     * content of every version beside them would grow the editor's first read
     * with the history - which is the one thing on that screen nobody reads
     * twice.
     */
    readVersion: (id, versionId) =>
      request(at(id, `/versions/${encodeURIComponent(versionId)}`), ADMIN_INIT).then((body) =>
        config.version.parse(body),
      ),

    /**
     * Moving one pointer onto one version - the rollback of §4, which copies
     * nothing and keeps the history linear.
     *
     * `production` moves only here; `développement` follows the last saved
     * version on its own and comes back here when it is pushed backwards by
     * hand.
     */
    movePointer: (id, input) => write(at(id, '/pointer'), 'POST', input),

    /**
     * The retirement, which is a soft delete and ALWAYS possible (§4) - even
     * when a version in production cites the entity. What the referents get in
     * exchange is a warning that says what to do about it.
     */
    retire: (id) => write(at(id, '/retirement'), 'POST'),

    /**
     * The restoration, from the card or from the history.
     *
     * Refused with a 409 `name-taken` when the name was taken while the entity
     * was away: the unicity of §4 holds among the entities that are NOT
     * retired, so coming back is what makes the collision, and the screen asks
     * for a rename first.
     */
    restore: (id) => write(at(id, '/retirement'), 'DELETE'),

    /**
     * « Dupliquer » (§4.2): a new entity and its v1, from the version the TAG
     * points at, written server side in one transaction.
     *
     * A TAG and never a version id, which is why the history offers no
     * « Dupliquer »: the gesture aims at a pointer like every reference of §4,
     * and a number is a handle nowhere else in the application.
     *
     * The answer is the COPY's detail, so the screen has the id of the editor
     * it is about to open and the name to select in it. The copy is never
     * refused for its name - it is calculated server side against the living
     * entities, so `name-taken` cannot reach an operator here - and the only
     * refusal this call can answer is the `pointer-empty` of a tag nothing sits
     * on.
     */
    duplicate: (id, tag) => write(at(id, '/duplicate'), 'POST', { tag }),
  };
}

/**
 * The criteria (§6), the first consumer of the factory.
 *
 * The ten stay exported under their own names: they are what the screens, the
 * capture script's neighbours and the tests already call, and a rename would
 * have been churn with no reader behind it.
 */
export const criteriaApi = createVersionedApi<
  CriterionList,
  CriterionDetail,
  CriterionVersion,
  CriterionSaveVersion,
  CriterionSaveDraft
>({
  basePath: '/api/criteria',
  list: criterionListSchema,
  detail: criterionDetailSchema,
  version: criterionVersionSchema,
});

export const fetchCriteria = criteriaApi.list;
export const createCriterion = criteriaApi.create;
export const fetchCriterion = criteriaApi.read;
export const renameCriterion = criteriaApi.rename;
export const saveCriterionDraft = criteriaApi.saveDraft;
export const saveCriterionVersion = criteriaApi.saveVersion;
export const fetchCriterionVersion = criteriaApi.readVersion;
export const moveCriterionPointer = criteriaApi.movePointer;
export const retireCriterion = criteriaApi.retire;
export const restoreCriterion = criteriaApi.restore;
export const duplicateCriterion = criteriaApi.duplicate;

/**
 * The personas (§10), the SECOND consumer of the factory - and the measure of
 * it: this block is `criteriaApi` with one noun changed.
 *
 * The ten stay exported under their own names, like the criterion's, because
 * that is what the page, the editor and the capture script of issue #42 will
 * call. A test that stubs the editor stubs `personasApi`, never the ten
 * bindings: they are references to the object, so replacing one of them would
 * leave the editor talking to the real backend.
 */
export const personasApi = createVersionedApi<
  PersonaList,
  PersonaDetail,
  PersonaVersion,
  PersonaSaveVersion,
  PersonaSaveDraft
>({
  basePath: '/api/personas',
  list: personaListSchema,
  detail: personaDetailSchema,
  version: personaVersionSchema,
});

export const fetchPersonas = personasApi.list;
export const createPersona = personasApi.create;
export const fetchPersona = personasApi.read;
export const renamePersona = personasApi.rename;
export const savePersonaDraft = personasApi.saveDraft;
export const savePersonaVersion = personasApi.saveVersion;
export const fetchPersonaVersion = personasApi.readVersion;
export const movePersonaPointer = personasApi.movePointer;
export const retirePersona = personasApi.retire;
export const restorePersona = personasApi.restore;
export const duplicatePersona = personasApi.duplicate;

/**
 * The protocols (§7), the THIRD consumer of the factory - and the first one
 * whose refusals are not all the pattern's.
 *
 * The ten calls are `criteriaApi` with one noun changed, like the persona's.
 * What is new is what comes BACK from two of them: `POST /versions` and
 * `POST /pointer` answer a 409 carrying `protocolErrorSchema` - `invalid-steps`
 * or `production-unresolved` - which is deliberately NOT one of the six codes of
 * `versionedErrorSchema` (§7.1: those are states of the CONTENT, not of the
 * base). They arrive through the same door as every other refusal: this file
 * unwraps nothing, `ApiError.details` keeps the body intact, and
 * `protocolRefusalSentence` in `protocol-labels.ts` words each of the two.
 *
 * The ten stay exported under their own names, like the two before them. A test
 * that stubs the editor stubs `protocolsApi`, never the ten bindings: they are
 * references to the object, so replacing one of them would leave the editor
 * talking to the real backend.
 */
export const protocolsApi = createVersionedApi<
  ProtocolList,
  ProtocolDetail,
  ProtocolVersion,
  ProtocolSaveVersion,
  ProtocolSaveDraft
>({
  basePath: '/api/protocols',
  list: protocolListSchema,
  detail: protocolDetailSchema,
  version: protocolVersionSchema,
});

export const fetchProtocols = protocolsApi.list;
export const createProtocol = protocolsApi.create;
export const fetchProtocol = protocolsApi.read;
export const renameProtocol = protocolsApi.rename;
export const saveProtocolDraft = protocolsApi.saveDraft;
export const saveProtocolVersion = protocolsApi.saveVersion;
export const fetchProtocolVersion = protocolsApi.readVersion;
export const moveProtocolPointer = protocolsApi.movePointer;
export const retireProtocol = protocolsApi.retire;
export const restoreProtocol = protocolsApi.restore;
export const duplicateProtocol = protocolsApi.duplicate;

/**
 * The experiences (§9), the FOURTH consumer of the factory - and the only one
 * with an ELEVENTH call beside the ten.
 *
 * The ten are `protocolsApi` with one noun changed, and the two refusals that
 * come back from `POST /versions` and `POST /pointer` follow the protocol's
 * doctrine exactly: a 409 carrying `experienceErrorSchema` - `invalid-timeline`
 * or `production-unresolved` - which is deliberately NOT one of the six codes of
 * `versionedErrorSchema` (§9.1: those are states of the CONTENT, not of the
 * base). This file unwraps nothing, `ApiError.details` keeps the body intact,
 * and `experienceRefusalSentence` in `experience-labels.ts` words each one.
 *
 * `checkExperience` is the eleventh, and it is not a crack in the pattern. §9.1
 * gives the save box one thing that is NOT a refusal - « un avertissement,
 * jamais un refus, quand une référence taguée production n'a pas encore de
 * version en production » - and a warning accompanies a write that SUCCEEDS, so
 * it can never travel as a 409. Two of the four refusals of §9.1 need the base
 * as well, so the editor cannot draw the box from what it holds alone. It
 * writes nothing, it hangs off the collection rather than off an entity, and it
 * therefore works for a creation as well as for a save.
 */
export const experiencesApi = createVersionedApi<
  ExperienceList,
  ExperienceDetail,
  ExperienceVersion,
  ExperienceSaveVersion,
  ExperienceSaveDraft
>({
  basePath: '/api/experiences',
  list: experienceListSchema,
  detail: experienceDetailSchema,
  version: experienceVersionSchema,
});

export const fetchExperiences = experiencesApi.list;
export const createExperience = experiencesApi.create;
export const fetchExperience = experiencesApi.read;
export const renameExperience = experiencesApi.rename;
export const saveExperienceDraft = experiencesApi.saveDraft;
export const saveExperienceVersion = experiencesApi.saveVersion;
export const fetchExperienceVersion = experiencesApi.readVersion;
export const moveExperiencePointer = experiencesApi.movePointer;
export const retireExperience = experiencesApi.retire;
export const restoreExperience = experiencesApi.restore;
export const duplicateExperience = experiencesApi.duplicate;

/** The dry run of §9.1: what the save box draws, and no write at all. */
export function checkExperience(input: ExperienceCheckRequest): Promise<ExperienceCheck> {
  return request('/api/experiences/check', {
    ...ADMIN_INIT,
    method: 'POST',
    body: JSON.stringify(input),
  }).then((body) => experienceCheckSchema.parse(body));
}

/**
 * The texts of the noyaux (noyau.md §6, issue #148), the FIFTH consumer of the
 * factory and its first SINGLETON (N8): Réglages > Noyaux.
 *
 * The server mounts six of the twelve routes (the discard of #185 among them),
 * under `/api/noyau-texts/{id}` with `NOYAU_TEXTS_ID` as the only `{id}`. So this
 * is the factory's object with two differences, both of them the singleton's:
 *
 *   - `create` is the FIRST « Enregistrer une nouvelle version »: there is no
 *     `POST /api/noyau-texts`, and the versions route writes the entity and its
 *     v1 in one transaction when none exists yet. The editor calls `create` on
 *     `detail === null` like every other object, and it lands there;
 *   - the six library gestures (`list`, `rename`, `retire`, `restore`,
 *     `duplicate`) would answer 404, and nothing calls them: the tab has no
 *     list and the `settings` logement of the layer renders none of the three.
 *     Their answer is left to the server rather than faked here, and the list
 *     has no schema to parse with - a parse that could only throw says so.
 */
const noyauTextsCalls = createVersionedApi<
  never,
  NoyauTextsDetail,
  NoyauTextsVersion,
  NoyauTextsSaveVersion,
  NoyauTextsSaveDraft
>({
  basePath: '/api/noyau-texts',
  list: {
    parse: () => {
      throw new Error('Les textes des noyaux sont un singleton : ils n’ont pas de liste (N8).');
    },
  },
  detail: noyauTextsDetailSchema,
  version: noyauTextsVersionSchema,
});

export const noyauTextsApi: VersionedApi<
  never,
  NoyauTextsDetail,
  NoyauTextsVersion,
  NoyauTextsSaveVersion,
  NoyauTextsSaveDraft
> = {
  ...noyauTextsCalls,
  create: (input) => noyauTextsCalls.saveVersion(NOYAU_TEXTS_ID, input),
};

/**
 * « Les modèles » and « Les règles » (vitrine.md section 5, issue #192): the
 * SIXTH and SEVENTH consumers of the pattern, the second and third SINGLETONS -
 * five routes each under `/api/model-settings/{id}` and
 * `/api/rule-settings/{id}`, with one constant as the only `{id}`.
 *
 * The object of the texts of the noyaux above, with ONE difference: the entity
 * is born at boot (`migrateModelSettingsAtBoot`, `migrateRuleSettingsAtBoot`),
 * so the detail never answers 404 and the editor never opens empty. `create`
 * stays the first « Enregistrer une nouvelle version » all the same, which is
 * what the layer calls on `detail === null` - a state these two never reach.
 * The six library gestures answer 404 on the server and nothing calls them.
 */
const modelSettingsCalls = createVersionedApi<
  never,
  ModelSettingsDetail,
  ModelSettingsVersion,
  ModelSettingsSaveVersion,
  ModelSettingsSaveDraft
>({
  basePath: '/api/model-settings',
  list: {
    parse: () => {
      throw new Error('Les modèles sont un singleton : ils n’ont pas de liste.');
    },
  },
  detail: modelSettingsDetailSchema,
  version: modelSettingsVersionSchema,
});

export const modelSettingsApi: VersionedApi<
  never,
  ModelSettingsDetail,
  ModelSettingsVersion,
  ModelSettingsSaveVersion,
  ModelSettingsSaveDraft
> = {
  ...modelSettingsCalls,
  create: (input) => modelSettingsCalls.saveVersion(MODEL_SETTINGS_ID, input),
};

/** The editor's read of « Les modèles »: the singleton, with its history and its draft. */
export function fetchModelSettings(): Promise<ModelSettingsDetail> {
  return modelSettingsApi.read(MODEL_SETTINGS_ID);
}

const ruleSettingsCalls = createVersionedApi<
  never,
  RuleSettingsDetail,
  RuleSettingsVersion,
  RuleSettingsSaveVersion,
  RuleSettingsSaveDraft
>({
  basePath: '/api/rule-settings',
  list: {
    parse: () => {
      throw new Error('Les règles sont un singleton : elles n’ont pas de liste.');
    },
  },
  detail: ruleSettingsDetailSchema,
  version: ruleSettingsVersionSchema,
});

export const ruleSettingsApi: VersionedApi<
  never,
  RuleSettingsDetail,
  RuleSettingsVersion,
  RuleSettingsSaveVersion,
  RuleSettingsSaveDraft
> = {
  ...ruleSettingsCalls,
  create: (input) => ruleSettingsCalls.saveVersion(RULE_SETTINGS_ID, input),
};

/** The editor's read of « Les règles »: the singleton, with its history and its draft. */
export function fetchRuleSettings(): Promise<RuleSettingsDetail> {
  return ruleSettingsApi.read(RULE_SETTINGS_ID);
}

/**
 * « Méthode » (analyste.md section 4, issue #196): the EIGHTH consumer of the
 * pattern and the fourth SINGLETON - five routes under `/api/method-settings/{id}`
 * with one constant as the only `{id}`, at the patron of the two above.
 *
 * Born at boot on the atelier from the seed of the code, so its detail never
 * answers 404 there; a vitrine receives it by « Passer en production » and answers
 * 404 until then (`fetchMethod`). The six library gestures answer 404 on the server
 * and nothing calls them.
 */
const methodCalls = createVersionedApi<
  never,
  MethodDetail,
  MethodVersion,
  MethodSaveVersion,
  MethodSaveDraft
>({
  basePath: '/api/method-settings',
  list: {
    parse: () => {
      throw new Error('Méthode est un singleton : elle n’a pas de liste.');
    },
  },
  detail: methodDetailSchema,
  version: methodVersionSchema,
});

export const methodApi: VersionedApi<
  never,
  MethodDetail,
  MethodVersion,
  MethodSaveVersion,
  MethodSaveDraft
> = {
  ...methodCalls,
  create: (input) => methodCalls.saveVersion(METHOD_ID, input),
};

/**
 * The read of « Méthode »: the singleton with its history and its draft, or `null`
 * while no version exists - a vitrine that has not received it yet. A 404 is the
 * empty state there and not an error, like the texts of the noyaux; every other
 * failure is left to the query.
 */
export async function fetchMethod(): Promise<MethodDetail | null> {
  try {
    return await methodApi.read(METHOD_ID);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/**
 * The editor's first read: the singleton, or `null` while no version exists.
 *
 * A 404 IS the empty state here and not an error - the entity is born on the
 * first save (N8), so « aucune version » is what the server answers until
 * then, and the tab opens its empty editor on it. Every other failure (a 401, a
 * 500, the network) is left to the query, which is where every screen of this
 * administration reads its errors.
 */
export async function fetchNoyauTexts(): Promise<NoyauTextsDetail | null> {
  try {
    return await noyauTextsApi.read(NOYAU_TEXTS_ID);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/**
 * Who reads which pointer of the texts, by the tag of each experience's
 * reference (noyau.md §6.5) - what the save box and « Passer en production »
 * of the tab show. The seventh route of the singleton, its own and not the
 * factory's, and it answers BEFORE the first version as well: the box of the
 * first save names the experiences that will start with it (review of !462).
 */
export function fetchNoyauTextsUsage(): Promise<NoyauTextsUsage> {
  return request(`/api/noyau-texts/${NOYAU_TEXTS_ID}/usage`, ADMIN_INIT).then((body) =>
    noyauTextsUsageSchema.parse(body),
  );
}

// ---------------------------------------------------------------------------
// The two reference lists of Réglages > Personas (§11.5)
// ---------------------------------------------------------------------------
// NOT versioned, so they go through none of the ten above: no draft, no
// pointers, no retirement, no history. Since issue #45 there is no per-row
// write either: a `GET` reads the list, a `PUT` on the COLLECTION saves the
// whole section in one transaction - a row with an `id` is a rewrite, one
// without is a creation, and for the scale the order of the array is the order
// of the positions - and a `DELETE` removes one row, alone, because it carries
// its own confirmation and its own count of personas. The tones keep a `POST`
// on top, and it is reserved for the « ajouter un ton » modal of the persona
// editor: that screen has never read the library, so it has no block to send.
//
// The refusals arrive in 409 with a `code` that `persona-labels.ts` words -
// never from the server's `message`, which is a developer's string.

/** The crans a persona's slider walks, in order, each with its count of personas. */
export function fetchInvolvementLevels(): Promise<InvolvementLevelList> {
  return request('/api/involvement-levels', ADMIN_INIT).then((body) =>
    involvementLevelListSchema.parse(body),
  );
}

/**
 * Saves the WHOLE scale in one request, applied in one transaction (#45).
 *
 * The array is the order, a row with an `id` is a rewrite, one without is a
 * creation. Everything lands or nothing does, which is what lets two crans
 * exchange their names: uniqueness is judged on the state the block describes.
 *
 * Nothing already saved moves: a version keeps the copy it froze. What a
 * rewrite does change is the NEXT version of every persona that follows that
 * cran without having redefined it (§10.1).
 */
export function saveInvolvementLevels(
  levels: readonly InvolvementLevelBlockRow[],
): Promise<InvolvementLevelList> {
  return request('/api/involvement-levels', {
    ...ADMIN_INIT,
    method: 'PUT',
    body: JSON.stringify({ levels }),
  }).then((body) => involvementLevelListSchema.parse(body));
}

/**
 * Deletes a cran. Refused in 409 `minimum-levels` when it would leave one.
 *
 * The answer carries the WHOLE scale back, because the positions behind the
 * removed cran have just been re-densified.
 */
export function deleteInvolvementLevel(id: string): Promise<InvolvementLevelList> {
  return request(`/api/involvement-levels/${encodeURIComponent(id)}`, {
    ...ADMIN_INIT,
    method: 'DELETE',
  }).then((body) => involvementLevelListSchema.parse(body));
}

/** The tones a persona's editor offers as a cloud of tags. */
export function fetchPersonaTones(): Promise<PersonaToneList> {
  return request('/api/persona-tones', ADMIN_INIT).then((body) =>
    personaToneListSchema.parse(body),
  );
}

/**
 * Adds ONE tone to the library, from the « ajouter un ton » modal of the
 * persona editor (§10.1).
 *
 * The one write of these two lists that is not a block, and it is the modal
 * that makes it one: that screen has never read the library, so it has no
 * block to send - and asking it for one would be asking it to rewrite rows it
 * never showed. Réglages saves its section through `savePersonaTones` below.
 */
export function createPersonaTone(input: PersonaToneWrite): Promise<PersonaToneEntry> {
  return request('/api/persona-tones', {
    ...ADMIN_INIT,
    method: 'POST',
    body: JSON.stringify(input),
  }).then((body) => personaToneEntrySchema.parse(body));
}

/**
 * Saves the WHOLE library in one request, applied in one transaction (#45).
 *
 * Same block as the scale, minus the order - the answer comes back sorted by
 * name whatever went out. Touches no persona: they keep their copy (§10.1).
 */
export function savePersonaTones(tones: readonly PersonaToneBlockRow[]): Promise<PersonaToneList> {
  return request('/api/persona-tones', {
    ...ADMIN_INIT,
    method: 'PUT',
    body: JSON.stringify({ tones }),
  }).then((body) => personaToneListSchema.parse(body));
}

/** Deletes a tone for good. No provenance is kept, so nothing else moves. */
export function deletePersonaTone(id: string): Promise<PersonaToneList> {
  return request(`/api/persona-tones/${encodeURIComponent(id)}`, {
    ...ADMIN_INIT,
    method: 'DELETE',
  }).then((body) => personaToneListSchema.parse(body));
}

/* ------------------------------------------------------------------ the NPC */

/**
 * The NPC (npc.md, issue #210): the FIFTH consumer of the factory, and the first with ONE side.
 *
 * Every route of this family answers 404 where the replay is closed (H2): a screen asks first
 * through the rail's probe, and a 404 that reaches one of these calls is read as « absent », never
 * as an error to retry. The versioned twelve are the pattern's; the calls below it are what an NPC
 * adds - its replays, the benches that carry it, its definitive deletion, the prefill of a
 * session - and the benches and the series, which are not versioned.
 */
export const npcsApi = createVersionedApi<
  NpcList,
  NpcDetail,
  NpcVersion,
  NpcSaveVersion,
  NpcSaveDraft
>({
  basePath: '/api/npcs',
  list: npcListSchema,
  detail: npcDetailSchema,
  version: npcVersionSchema,
});

export const fetchNpcs = npcsApi.list;
export const fetchNpc = npcsApi.read;
export const fetchNpcVersion = npcsApi.readVersion;
export const restoreNpc = npcsApi.restore;
export const duplicateNpc = npcsApi.duplicate;

/** `GET /api/npcs/{id}/replays` (§10): the tab « Rejeux », most recent first. */
export function fetchNpcReplays(id: string): Promise<NpcReplayList> {
  return request(`/api/npcs/${encodeURIComponent(id)}/replays`, ADMIN_INIT).then((body) =>
    npcReplayListSchema.parse(body),
  );
}

/** `GET /api/npcs/{id}/referents` (§12): the benches that carry the NPC, retired ones too. */
export function fetchNpcReferents(id: string): Promise<NpcReferents> {
  return request(`/api/npcs/${encodeURIComponent(id)}/referents`, ADMIN_INIT).then((body) =>
    npcReferentsSchema.parse(body),
  );
}

/** `DELETE /api/npcs/{id}` (§3.5): the DEFINITIVE deletion, which sends its replays to the trash. */
export function deleteNpc(id: string): Promise<NpcDeleted> {
  return request(`/api/npcs/${encodeURIComponent(id)}`, { ...ADMIN_INIT, method: 'DELETE' }).then(
    (body) => npcDeletedSchema.parse(body),
  );
}

/**
 * `GET /api/npcs/prefill?session=` (§4.2): the draft « Créer un NPC » opens, or a 409
 * `prefill-refused` whose `reason` says why - read from `ApiError.details`, never from a message.
 */
export function fetchNpcPrefill(sessionId: string): Promise<NpcPrefill> {
  return request(`/api/npcs/prefill?session=${encodeURIComponent(sessionId)}`, ADMIN_INIT).then(
    (body) => npcPrefillSchema.parse(body),
  );
}

/** `GET /api/npc-benches` (§5): every bench, retired ones included - the page filters. */
export function fetchNpcBenches(): Promise<NpcBenchList> {
  return request('/api/npc-benches', ADMIN_INIT).then((body) => npcBenchListSchema.parse(body));
}

/** `POST /api/npc-benches`: a bench is born with its name and its ordered list. */
export function createNpcBench(input: NpcBenchWrite): Promise<NpcBench> {
  return request('/api/npc-benches', {
    ...ADMIN_INIT,
    method: 'POST',
    body: JSON.stringify(input),
  }).then((body) => npcBenchSchema.parse(body));
}

/** `PUT /api/npc-benches/{id}`: the bench is written WHOLE - it has no versions (H4). */
export function saveNpcBench(id: string, input: NpcBenchWrite): Promise<NpcBench> {
  return request(`/api/npc-benches/${encodeURIComponent(id)}`, {
    ...ADMIN_INIT,
    method: 'PUT',
    body: JSON.stringify(input),
  }).then((body) => npcBenchSchema.parse(body));
}

/** `POST /api/npc-benches/{id}/retirement`: « Retirer ce banc ». */
export function retireNpcBench(id: string): Promise<NpcBench> {
  return request(`/api/npc-benches/${encodeURIComponent(id)}/retirement`, {
    ...ADMIN_INIT,
    method: 'POST',
  }).then((body) => npcBenchSchema.parse(body));
}

/** `DELETE /api/npc-benches/{id}/retirement`: restores a bench, refused when its name was taken. */
export function restoreNpcBench(id: string): Promise<NpcBench> {
  return request(`/api/npc-benches/${encodeURIComponent(id)}/retirement`, {
    ...ADMIN_INIT,
    method: 'DELETE',
  }).then((body) => npcBenchSchema.parse(body));
}

/** `GET /api/npc-series` (§9): the tab « Séries », most recent first. */
export function fetchNpcSeries(): Promise<NpcSeriesList> {
  return request('/api/npc-series', ADMIN_INIT).then((body) => npcSeriesListSchema.parse(body));
}

/** `GET /api/npc-series/{id}` (§9): the page of a series, its slots grouped by NPC and by pass. */
export function fetchNpcSeriesDetail(id: string): Promise<NpcSeriesDetail> {
  return request(`/api/npc-series/${encodeURIComponent(id)}`, ADMIN_INIT).then((body) =>
    npcSeriesDetailSchema.parse(body),
  );
}

/**
 * `POST /api/npc-series/estimate` (§6.2): what a launch would cost, without launching anything. A
 * refusal (`no-protocol-page`, `bench-empty`...) is the same 409 the launch would answer.
 */
export function estimateNpcLaunch(input: NpcLaunch): Promise<NpcEstimate> {
  return request('/api/npc-series/estimate', {
    ...ADMIN_INIT,
    method: 'POST',
    body: JSON.stringify(input),
  }).then((body) => npcEstimateSchema.parse(body));
}

/** `POST /api/npc-series` (§6.3): the launch. 201 with the series, and the NPC it skipped (H24). */
export function launchNpcSeries(input: NpcLaunch): Promise<NpcSeriesLaunched> {
  return request('/api/npc-series', {
    ...ADMIN_INIT,
    method: 'POST',
    body: JSON.stringify(input),
  }).then((body) => npcSeriesLaunchedSchema.parse(body));
}

/**
 * `POST /api/npc-series/{id}/stop` (§9): « Arrêter la série ». Replayable, and it may take up to
 * thirty seconds - the runner waits for the turn in flight of each replay it interrupts.
 */
export function stopNpcSeries(id: string): Promise<NpcSeriesDetail> {
  return request(`/api/npc-series/${encodeURIComponent(id)}/stop`, {
    ...ADMIN_INIT,
    method: 'POST',
  }).then((body) => npcSeriesDetailSchema.parse(body));
}

/** `POST /api/npc-series/{id}/relaunch` (§7.3): « Relancer les rejeux interrompus ». */
export function relaunchNpcSeries(id: string): Promise<NpcSeriesDetail> {
  return request(`/api/npc-series/${encodeURIComponent(id)}/relaunch`, {
    ...ADMIN_INIT,
    method: 'POST',
  }).then((body) => npcSeriesDetailSchema.parse(body));
}

/** `GET /api/replays/{id}/watch` (§11): the person's screen of a replay, read every two seconds. */
export function fetchReplayWatch(replayId: string): Promise<NpcWatch> {
  return request(`/api/replays/${encodeURIComponent(replayId)}/watch`, ADMIN_INIT).then((body) =>
    npcWatchSchema.parse(body),
  );
}

/** `GET /api/replays/{id}/playback` (§11): the recorded sequence of a replay that is over. */
export function fetchReplayPlayback(replayId: string): Promise<NpcPlayback> {
  return request(`/api/replays/${encodeURIComponent(replayId)}/playback`, ADMIN_INIT).then((body) =>
    npcPlaybackSchema.parse(body),
  );
}
